import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

export interface Card {
  id: string
  name: string
  card_type: 'credit' | 'debit' | 'prepaid'
  color: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

interface CardListResponse {
  cards: Card[]
}

export type CardResponse = Card

export interface CreateCardData {
  name: string
  card_type: 'credit' | 'debit' | 'prepaid'
  color?: string | null
}

export interface UpdateCardData {
  name?: string
  card_type?: 'credit' | 'debit' | 'prepaid'
  color?: string | null
}

export function useCards() {
  return useQuery<CardListResponse>({
    queryKey: ['cards'],
    queryFn: async () => {
      const res = await api.get<CardListResponse>('/cards/')
      return res.data
    },
  })
}

export function useCreateCard() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (newCard: CreateCardData) => {
      const res = await api.post<CardResponse>('/cards/', newCard)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] })
    },
  })
}

export function useUpdateCard() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateCardData }) => {
      const res = await api.patch<CardResponse>(`/cards/${id}`, updates)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] })
    },
  })
}

export function useArchiveCard() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (cardId: string) => {
      const res = await api.delete<{ message: string; card_id: string }>(`/cards/${cardId}`)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] })
    },
  })
}

export function useCard(cardId: string) {
  return useQuery<CardResponse>({
    queryKey: ['cards', cardId],
    queryFn: async () => {
      const res = await api.get<CardResponse>(`/cards/${cardId}`)
      return res.data
    },
    enabled: !!cardId,
  })
}
